import * as React from "react";
import styled from "@emotion/styled";
import { fontFamily, palette, size, space } from "@stedi/dls/constants";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import "../lib/prismjs/jsonata";

export interface ReadonlyPreviewProps extends React.HTMLAttributes<HTMLPreElement> {
  code: string;
  language?: string;
}

const SPre = styled.pre({
  height: "100%",
  width: "100%",
  fontSize: size.base,
  margin: 0,
  paddingLeft: space.s3 + space.s2,
  fontFamily: fontFamily.mono,
  lineHeight: 1.5,
  fontWeight: 400,
  tabSize: 4,
  hyphens: "none",
  cursor: "text",
  letterSpacing: "normal",
  textSizeAdjust: "100%",
  overflow: "auto",
  code: {
    position: "relative",
    wordBreak: "break-all",
  },
  ".language-jsonata": {
    color: palette.gray13,

    ".token": {
      "&.jsonata-string": {
        color: palette.green,
      },
      "&.jsonata-comment": {
        color: palette.gray10,
      },
      "&.jsonata-variable": {
        color: palette.red10,
      },
      "&.jsonata-name": {
        color: palette.blue,
      },
    },
  },
  ".token": {
    "&.comment, &.prolog, &.doctype, &.cdata, &.punctuation, &.namespace, &.operator, &.entity, &.url, &.regex, &.important, &.variable":
      {
        color: palette.gray13,
      },
    "&.property, &.tag, &.constant, &.symbol, &.deleted": {
      color: palette.blue,
    },
    "&.selector, &.attr-name, &.builtin, &.inserted": {
      color: palette.blue,
    },
    "&.string, &.char": {
      color: palette.magenta,
    },
    "&.number": {
      color: palette.green,
    },
    "&.atrule, &.attr-value, &.keyword": {
      color: palette.red10,
    },
    "&.function, &.class-name, &.boolean": {
      color: palette.magenta,
    },
  },
  "*": {
    // Adapted from Monaco
    fontFamily:
      "Recursive, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif !important",
  },
});

const ReadonlyPreview = ({ code, language = "json", ...rest }: ReadonlyPreviewProps) => {
  const elementRef = React.useRef<HTMLPreElement>(null);

  React.useEffect(() => {
    try {
      if (!elementRef.current) return;
      Prism.highlightElement(elementRef.current);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [code]);

  return (
    <SPre {...rest} data-testid={`readonly-preview-language-${language}`}>
      <code ref={elementRef} className={`language-${language}`}>
        {code}
      </code>
    </SPre>
  );
};

export { ReadonlyPreview };
